import React, { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../config';
import { IconAlert } from '../components/Icons';
import './AdminFeedback.css';
import './AdminMetricsHistory.css';
import './AdminAlerts.css';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';

function fmtMoney(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

function alertKindLabel(kind) {
  if (kind === 'source_mismatch') return 'Data mismatch';
  if (kind === 'drift') return 'History drift';
  if (kind === 'action_impact') return 'Large edit impact';
  return kind;
}

function alertHeadline(alert) {
  const d = alert.details || {};
  if (alert.kind === 'source_mismatch') {
    const missing = d.missingInResults?.length || 0;
    const sheetDiff = d.sheetMismatchCount || 0;
    if (missing && sheetDiff) {
      return `${d.issueCount} issue(s): ${missing} webhook day(s) missing from Results; ${sheetDiff} sheet mismatch(es).`;
    }
    if (missing) {
      return `${missing} trading day(s) have NT8 webhook P&L but nothing in Results / Strategy Data overlay.`;
    }
    if (sheetDiff) {
      return `${sheetDiff} day(s) where Results Spreadsheet and live Results disagree.`;
    }
    return `${d.issueCount || 0} data source issue(s) detected.`;
  }
  if (alert.kind === 'drift') {
    return `History rewritten: equity as of ${d.asOfDate} changed ${fmtMoney(d.deltaDollars)} `
      + `(${fmtMoney(d.equityWas)} → ${fmtMoney(d.equityNow)}) since the ${d.previousSnapshotDate} snapshot`;
  }
  if (alert.kind === 'action_impact') {
    const delta = d.deltas?.fixedReturnPct;
    const sign = delta > 0 ? '+' : '';
    return `${d.action || 'Edit'} moved total return by ${sign}${Number(delta).toFixed(2)}% (${d.actorEmail || 'unknown admin'})`;
  }
  return alert.kind;
}

function SourceMismatchDetails({ details }) {
  const issues = details?.issues || [];
  if (!issues.length) return null;
  return (
    <div className="admin-alerts-issue-table-wrap">
      <table className="admin-feedback-table admin-alerts-issue-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Issue</th>
            <th className="num">Webhook</th>
            <th className="num">Journal</th>
            <th className="num">Sheet</th>
            <th className="num">Results</th>
          </tr>
        </thead>
        <tbody>
          {issues.map((row) => (
            <tr key={`${row.type}-${row.date}`}>
              <td>{row.date}</td>
              <td>{row.type === 'missing_in_results' ? 'Missing in Results' : 'Sheet vs Results'}</td>
              <td className="num">{fmtMoney(row.webhook)}</td>
              <td className="num">{fmtMoney(row.journal)}</td>
              <td className="num">{fmtMoney(row.sheet)}</td>
              <td className="num">{fmtMoney(row.results)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminAlerts({ onNavigate }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [running, setRunning] = useState(false);
  const [refillingId, setRefillingId] = useState(null);
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    ...getBearerAuthHeaders(),
  });

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 100 };
      if (showAcknowledged) params.all = 'true';
      const res = await fetch(API_ENDPOINTS.adminAlerts(params), { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load alerts');
      setAlerts(data.alerts || []);
    } catch (err) {
      setError(err.message);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [showAcknowledged]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const runReconciliation = async () => {
    setRunning(true);
    setMessage('');
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminAlertsRunReconciliation, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Reconciliation failed');
      setMessage(
        `Checked ${data.strategiesChecked} NT8-mapped strategies — `
        + `${data.alertsRaised} new alert(s), ${data.issueCount} issue(s) found.`,
      );
      await fetchAlerts();
      window.dispatchEvent(new CustomEvent('admin-alerts-changed'));
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  };

  const ackAlert = async (id) => {
    try {
      await fetch(API_ENDPOINTS.adminAlertById(id), { method: 'PATCH', headers: authHeaders() });
      setAlerts((prev) => prev.filter((a) => a.id !== id));
      window.dispatchEvent(new CustomEvent('admin-alerts-changed'));
    } catch (_e) { /* refresh restores */ }
  };

  const refillMissingDays = async (alert) => {
    const missingCount = alert.details?.missingInResults?.length
      || (alert.details?.issues || []).filter((row) => row.type === 'missing_in_results').length;
    if (!missingCount) return;
    setRefillingId(alert.id);
    setMessage('');
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminAlertRefillMissingDays(alert.id), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Refill failed');
      const inserted = data.backfill?.inserted ?? 0;
      const remaining = data.remainingMissing ?? null;
      setMessage(
        `Refilled ${inserted} webhook trade row(s) for ${alert.strategy_name}`
        + (remaining != null ? ` — ${remaining} day(s) still missing in Results.` : '.'),
      );
      await fetchAlerts();
    } catch (err) {
      setError(err.message);
    } finally {
      setRefillingId(null);
    }
  };

  const canRefillAlert = (alert) => {
    if (alert.kind !== 'source_mismatch' || alert.acknowledged) return false;
    const missing = alert.details?.missingInResults?.length
      || (alert.details?.issues || []).filter((row) => row.type === 'missing_in_results').length;
    return missing > 0;
  };

  const unacknowledged = alerts.filter((a) => !a.acknowledged);

  return (
    <div className="admin-feedback-page admin-alerts-page">
      <div className="admin-feedback-content">
        <div className="admin-feedback-header">
          <h1 className="admin-feedback-title">
            <IconAlert size={24} />
            Alerts
          </h1>
          <div className="admin-feedback-filters">
            <label className="admin-alerts-toggle">
              <input
                type="checkbox"
                checked={showAcknowledged}
                onChange={(e) => setShowAcknowledged(e.target.checked)}
              />
              Show acknowledged
            </label>
            <button
              type="button"
              className="admin-feedback-bulk-btn"
              onClick={runReconciliation}
              disabled={running}
            >
              {running ? 'Checking…' : 'Run data check now'}
            </button>
          </div>
        </div>

        <p className="admin-logs-intro">
          Cross-source checks for NT8-mapped strategies: compares Strategy Data journal rows, NT8 webhook
          trades, the Results Spreadsheet, and what Results actually shows. Also lists metric drift and
          large admin-edit alerts from Metrics History. Runs automatically after the 5:30 PM ET snapshot.
        </p>

        {message ? <p className="admin-metrics-message">{message}</p> : null}
        {error ? (
          <div className="admin-feedback-error"><IconAlert size={18} />{error}</div>
        ) : null}

        {loading ? (
          <p className="admin-feedback-loading">Loading alerts…</p>
        ) : unacknowledged.length === 0 && !showAcknowledged ? (
          <p className="admin-feedback-empty">No unacknowledged alerts. Click “Run data check now” to scan NT8-mapped strategies.</p>
        ) : (
          <div className="admin-metrics-alerts">
            {(showAcknowledged ? alerts : unacknowledged).map((a) => (
              <div
                key={a.id}
                className={`admin-metrics-alert admin-metrics-alert-${a.kind}${a.acknowledged ? ' is-acknowledged' : ''}`}
              >
                <div className="admin-alerts-alert-body">
                  <div className="admin-alerts-alert-meta">
                    <strong>{a.strategy_name}</strong>
                    <span className="admin-metrics-alert-kind">{alertKindLabel(a.kind)}</span>
                    <span className="admin-metrics-alert-date">{a.alert_date}</span>
                    <span className="admin-alerts-created">{fmtDateTime(a.created_at)}</span>
                  </div>
                  <p>{alertHeadline(a)}</p>
                  {a.kind === 'source_mismatch' && (
                    <>
                      <button
                        type="button"
                        className="admin-alerts-expand-btn"
                        onClick={() => setExpandedId((id) => (id === a.id ? null : a.id))}
                      >
                        {expandedId === a.id ? 'Hide details' : 'Show day-by-day details'}
                      </button>
                      {expandedId === a.id ? <SourceMismatchDetails details={a.details} /> : null}
                    </>
                  )}
                </div>
                {!a.acknowledged ? (
                  <div className="admin-alerts-actions">
                    {canRefillAlert(a) ? (
                      <button
                        type="button"
                        className="admin-feedback-bulk-btn admin-alerts-refill-btn"
                        onClick={() => refillMissingDays(a)}
                        disabled={refillingId === a.id}
                      >
                        {refillingId === a.id ? 'Refilling…' : 'Refill missing days'}
                      </button>
                    ) : null}
                    <button type="button" className="admin-feedback-bulk-btn" onClick={() => ackAlert(a.id)}>
                      Acknowledge
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}

        <p className="admin-alerts-footer">
          Tip: use <strong>Refill missing days</strong> on data mismatch alerts to copy NT8 webhook trades into the
          Strategy Data journal, or open{' '}
          <button type="button" className="admin-alerts-inline-link" onClick={() => onNavigate && onNavigate('admin-scenario-data')}>Strategy Data</button>
          {' '}to edit rows manually.
        </p>
      </div>
    </div>
  );
}
